import React, { useCallback, useMemo } from 'react';
import {
  View,
  Pressable,
  ScrollView,
  Platform,
  StyleSheet,
  TextInput,
} from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';

const DISCOVERY_CHIP_HEIGHT = 34;

/**
 * Catalog discovery stack (tasks-style):
 *   1. Topline → 2. Headline → 3. Optional subtitle
 *   4. Catalog chips (+ optional custom chip)
 *   5. Optional `children` (e.g. animated demos, bento, etc.)
 *   6. Optional search field
 *   7. “Try:” starter chips
 */
function CatalogDiscoveryHero({
  topline,
  headline,
  /** Shown under the headline when truthy. */
  subtitle,
  /** Optional override for subtitle color (e.g. higher contrast on dark pages). */
  subtitleColor,
  children,
  catalogItems = [],
  onCatalogItemPress,
  /** `{ label, onPress, icon? }` — top-right action beside the topline. */
  headerAction = null,
  /** `{ label, onPress }` — trailing “+ …” chip. */
  customChip = null,
  /** When true, the custom chip renders with primary/accent styling so it
   *  stands out as the page's main call-to-action (used by the tasks page
   *  where no template suggestion chips sit beside it). */
  customChipPrimary = false,
  chipAccentColor = null,
  /**
   * When provided, renders the rounded search row above try chips.
   * @type {{ value: string, onChangeText: (t: string) => void, placeholder?: string, autoCorrect?: boolean, testID?: string } | null}
   */
  search = null,
  tryChips = [],
  onTryQuery,
  tryPrefix = 'Try:',
}) {
  const { colors } = useTheme();
  const { isXS, isSM } = useResponsiveness();

  const showSearch = search && typeof search.onChangeText === 'function';
  const showTry = Array.isArray(tryChips) && tryChips.length > 0 && typeof onTryQuery === 'function';
  const showSubtitle = !!(subtitle && String(subtitle).trim());
  const showHeadline = !!(headline && String(headline).trim());
  const hasCatalogChips = catalogItems.length > 0 || !!customChip?.onPress;
  const hasChildren = children != null && children !== false;
  const showHeaderAction = typeof headerAction?.onPress === 'function';

  // Single source of truth for the hero's horizontal padding — horizontal
  // chip scrollers below use it both as a negative margin (to bleed the
  // ScrollView to the screen edges so a partial last chip reads as a
  // "more here" peek instead of a hard clip) and as internal padding (so
  // the first chip still aligns with the headline/subtitle indent).
  const wrapPaddingX = isXS ? 15 : isSM ? 15 : 20;

  const styles = useMemo(() => StyleSheet.create({
    wrap: {
      width: '100%',
      marginBottom: 10,
      paddingHorizontal: wrapPaddingX,
    },
    inner: {
      width: '100%',
      alignSelf: 'stretch',
      paddingVertical: 0,
    },
    toplineRow: {
      width: '100%',
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 12,
    },
    topline: {
      flex: 1,
      minWidth: 0,
      fontSize: isXS ? 26 : 34,
      lineHeight: isXS ? 28 : 38,
      letterSpacing: -0.5,
      textAlign: 'left',
    },
    headerAction: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      flexShrink: 0,
      marginTop: isXS ? 4 : 6,
      paddingHorizontal: isXS ? 10 : 12,
      height: 32,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
      ...(Platform.OS === 'web' && { cursor: 'pointer' }),
    },
    headline: {
      width: '100%',
      fontSize: isXS ? 26 : 34,
      lineHeight: isXS ? 28 : 38,
      letterSpacing: -0.5,
      textAlign: 'left',
    },
    subtitle: {
      width: '100%',
      marginTop: 10,
      marginBottom: !hasCatalogChips ? 0 : 14,
      fontSize: isXS ? 14 : 15,
      lineHeight: isXS ? 20 : 22,
      textAlign: 'left',
    },
    chipRow: {
      width: '100%',
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: 8,
      minHeight: DISCOVERY_CHIP_HEIGHT,
      marginBottom: 4,
    },
    chipRowScroll: {
      height: DISCOVERY_CHIP_HEIGHT,
      marginBottom: 4,
      // Bleed the scroller to the screen edges so a partial trailing chip
      // peeks past the right edge as a scroll affordance instead of being
      // clipped flush against the hero's padding.
      marginLeft: -wrapPaddingX,
      marginRight: -wrapPaddingX,
    },
    chipRowScrollContent: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      // Re-add the indent inside the scroller so the first chip still
      // lines up with the headline/subtitle on the left.
      paddingLeft: wrapPaddingX,
      paddingRight: wrapPaddingX,
      minHeight: DISCOVERY_CHIP_HEIGHT,
    },
    chip: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      height: DISCOVERY_CHIP_HEIGHT,
      paddingHorizontal: 12,
      paddingVertical: 0,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
    },
    chipCustom: {
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
    },
    chipCustomPrimary: {
      borderColor: colors.accent_color_primary,
      backgroundColor: colors.accent_color_primary,
      paddingHorizontal: 16,
    },
    childrenSlot: {
      width: '100%',
      marginTop: 15,
      marginBottom: 20,
    },
    searchWrap: {
      width: '100%',
      marginBottom: 10,
    },
    searchInputContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      marginTop: hasChildren ? 0 : 15,
      height: isXS ? 40 : 44,
      paddingHorizontal: 14,
      borderRadius: 10,
      backgroundColor: colors.solid_color_block,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
    },
    searchInput: {
      flex: 1,
      fontSize: 14,
      color: colors.text_color_primary,
      ...(Platform.OS === 'web' && { outlineStyle: 'none' }),
    },
    tryScroll: {
      // Same edge bleed as the catalog chip scroller — horizontal scroll
      // reaches the screen edges so cut-off try chips read as a peek
      // rather than a clip.
      marginLeft: -wrapPaddingX,
      marginRight: -wrapPaddingX,
    },
    tryRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingTop: 2,
      paddingLeft: wrapPaddingX,
      paddingRight: wrapPaddingX,
    },
    tryChip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingHorizontal: 8,
      height: 24,
      borderRadius: 12,
    },
  }), [colors, isXS, hasChildren, hasCatalogChips, wrapPaddingX]);

  const catalogChips = useMemo(
    () => (
      <>
        {catalogItems.map((item) => (
          <Pressable
            key={String(item.id ?? item.slug)}
            style={[
              styles.chip,
              chipAccentColor && {
                borderColor: `${chipAccentColor}66`,
                backgroundColor: `${chipAccentColor}12`,
              },
            ]}
            onPress={() => onCatalogItemPress?.(item)}
          >
            {item.icon ? (
              <Icon
                name={item.icon}
                size={12}
                color={chipAccentColor || colors.text_color_secondary}
                regular
              />
            ) : null}
            <Text tag="span" size="s" weight="medium" numberOfLines={1}>{item.name}</Text>
          </Pressable>
        ))}
        {customChip?.onPress && (
          <Pressable
            style={[
              styles.chip,
              customChipPrimary ? styles.chipCustomPrimary : styles.chipCustom,
            ]}
            onPress={customChip.onPress}
          >
            <Icon
              name="plus"
              size={12}
              color={customChipPrimary ? colors.accent_text_color : colors.text_color_primary}
            />
            <Text
              tag="span"
              size="s"
              weight="bold"
              style={customChipPrimary ? { color: colors.accent_text_color } : undefined}
              color={customChipPrimary ? undefined : 'primary'}
              numberOfLines={1}
            >
              {customChip.label || 'Custom'}
            </Text>
          </Pressable>
        )}
      </>
    ),
    [
      catalogItems,
      chipAccentColor,
      colors.text_color_primary,
      colors.text_color_secondary,
      customChip,
      customChipPrimary,
      onCatalogItemPress,
      styles.chip,
      styles.chipCustom,
      styles.chipCustomPrimary,
    ],
  );

  const clearSearch = useCallback(() => {
    search?.onChangeText?.('');
  }, [search]);

  return (
    <View style={styles.wrap}>
      <View style={styles.inner}>
        {(topline || showHeaderAction) ? (
          <View style={styles.toplineRow}>
            {topline ? (
              <Text tag="p" weight="extraBold" color="tertiary" style={styles.topline}>
                {topline}
              </Text>
            ) : (
              <View style={styles.topline} />
            )}
            {showHeaderAction ? (
              <Pressable
                style={styles.headerAction}
                onPress={headerAction.onPress}
                accessibilityRole="button"
                accessibilityLabel={headerAction.label}
              >
                {headerAction.icon ? (
                  <Icon
                    name={headerAction.icon}
                    size={12}
                    color={colors.text_color_primary}
                  />
                ) : null}
                <Text size="xs" weight="semibold" numberOfLines={1}>
                  {headerAction.label}
                </Text>
              </Pressable>
            ) : null}
          </View>
        ) : null}
        {showHeadline ? (
          <Text
            tag="h2"
            weight="extraBold"
            style={[styles.headline, !showSubtitle && (hasCatalogChips || children != null) && { marginBottom: 12 }]}
          >
            {headline}
          </Text>
        ) : null}
        {showSubtitle && (
          <Text
            tag="p"
            size="n"
            color={subtitleColor ? undefined : 'secondary'}
            style={[styles.subtitle, subtitleColor ? { color: subtitleColor } : null]}
          >
            {subtitle}
          </Text>
        )}

        {hasCatalogChips && (
          isXS ? (
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              style={styles.chipRowScroll}
              contentContainerStyle={styles.chipRowScrollContent}
            >
              {catalogChips}
            </ScrollView>
          ) : (
            <View style={styles.chipRow}>
              {catalogChips}
            </View>
          )
        )}

        {hasChildren && (
          <View style={styles.childrenSlot}>
            {children}
          </View>
        )}

        {showSearch && (
          <View style={styles.searchWrap}>
            <View style={styles.searchInputContainer}>
              <Icon name="search" size={14} color={colors.text_color_secondary} />
              <TextInput
                value={search.value ?? ''}
                onChangeText={search.onChangeText}
                placeholder={search.placeholder}
                placeholderTextColor={colors.text_color_tertiary}
                style={styles.searchInput}
                autoCorrect={search.autoCorrect !== false}
                autoCapitalize={search.autoCapitalize ?? 'none'}
                testID={search.testID}
                clearButtonMode={Platform.OS === 'ios' ? 'while-editing' : undefined}
                {...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {})}
              />
              {!!(search.value && String(search.value).length > 0) && (
                <Pressable onPress={clearSearch} hitSlop={6}>
                  <Icon name="xmark" size={13} color={colors.text_color_tertiary} />
                </Pressable>
              )}
            </View>
          </View>
        )}

        {showTry && (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.tryScroll}
            contentContainerStyle={styles.tryRow}
          >
            <Text tag="span" size="xs" color="tertiary" style={{ marginRight: 5, alignSelf: 'center' }}>
              {tryPrefix}
            </Text>
            {tryChips.map((s) => {
              const chipColor = s.color || colors.text_color_secondary;
              const chipBg = s.color ? `${s.color}14` : colors.solid_color_secondary;
              return (
                <Pressable
                  key={s.label}
                  onPress={() => onTryQuery(s.query)}
                  style={[
                    styles.tryChip,
                    { backgroundColor: chipBg },
                    Platform.OS === 'web' && { cursor: 'pointer' },
                  ]}
                >
                  <Icon name={s.icon} size={10} color={chipColor} />
                  <Text tag="span" size="xs" weight="semibold" style={{ color: chipColor }}>{s.label}</Text>
                </Pressable>
              );
            })}
          </ScrollView>
        )}
      </View>
    </View>
  );
}

export default React.memo(CatalogDiscoveryHero);
